import { Injectable } from '@angular/core';
import { TaskBaseApi, TaskBase } from 'src/api';
import { Observable, Subject, combineLatest } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Omit } from 'lodash';
import { BackendResponse } from '@schenck/rotec-components';

export enum TaskEvent {
  Created = 'TaskCreated',
  Updated = 'TaskUpdated',
  Deleted = 'TaskDeleted',
  Shifted = 'TaskShifted',
  Skipped = 'TaskSkipped',
  Done = 'TaskDone',
  Reactivated = 'TaskReactivated',
}

@Injectable()
export class TaskBaseService<Task extends TaskBase> {
  private _taskChange$ = new Subject<TaskEvent>();
  /**
   * Emits whenever there was a change to a task.
   * This includes shift, skip, done, reactivate, create, update, delete.
   */
  taskChange$: Observable<TaskEvent> = this._taskChange$.asObservable();

  constructor(protected taskApi: TaskBaseApi<Task>) {}

  /**
   * Search for (in)active all tasks in a given time range.
   */
  search$(active: boolean, date: { from: Date; to: Date } | null): Observable<Task[]> {
    return this.taskApi.search$(active, date);
  }

  /**
   * Gets a single task.
   */
  get$(id: string): Observable<Task> {
    return this.taskApi.get$(id);
  }

  /**
   * Creates a new task.
   */
  create$(task: Omit<Task, 'id'>): Observable<BackendResponse.Response<Task>> {
    return this.taskApi.create$(task).pipe(tap((resp) => this.emitChange(TaskEvent.Created, resp)));
  }

  /**
   * Uploads given file to server to create multiple tasks at once.
   * Task definitions are read from file.
   */
  createMultipleFromFile$(file: File, assetId: string): Observable<BackendResponse.Response<void>> {
    return this.taskApi
      .createMultipleFromFile$(file, assetId)
      .pipe(tap((resp) => this.emitChange(TaskEvent.Created, resp)));
  }

  /**
   * Updates a task.
   *
   * Returns null if that task has changed in such a way that it is not
   * possible anymore to return a correct model.
   * Example: You change the scheduling
   * -> after that all tasks are re-generated, single task not possible to infer from old task.
   */
  update$(task: Task): Observable<BackendResponse.Response<Task | null>> {
    return this.taskApi.update$(task).pipe(tap((resp) => this.emitChange(TaskEvent.Updated, resp)));
  }

  /**
   * Deletes given task.
   */
  delete$(id: string): Observable<BackendResponse.Response<void>> {
    return this.taskApi.delete$(id).pipe(tap((resp) => this.emitChange(TaskEvent.Deleted, resp)));
  }

  /**
   * Deletes given tasks.
   */
  deleteMultiple$(ids: string[]) {
    return combineLatest(ids.map((id) => this.taskApi.delete$(id))).pipe(
      tap((responses) => {
        if (responses.some(BackendResponse.isSuccess)) {
          this.emitChange(TaskEvent.Deleted);
        }
      })
    );
  }

  /**
   * Reactivates a task.
   */
  undo$(id: string): Observable<BackendResponse.Response<void>> {
    return this.taskApi.undo$(id).pipe(tap((resp) => this.emitChange(TaskEvent.Reactivated, resp)));
  }

  /**
   * Sets task to done.
   */
  done$(id: string, reason: string): Observable<BackendResponse.Response<void>> {
    return this.taskApi
      .done$(id, reason)
      .pipe(tap((resp) => this.emitChange(TaskEvent.Done, resp)));
  }

  /**
   * Shifts task.
   */
  shift$(
    id: string,
    reason: string,
    shiftedDueDate: Date
  ): Observable<BackendResponse.Response<void>> {
    return this.taskApi
      .shift$(id, reason, shiftedDueDate)
      .pipe(tap((resp) => this.emitChange(TaskEvent.Shifted, resp)));
  }

  /**
   * Skips task.
   */
  skip$(id: string, reason: string): Observable<BackendResponse.Response<void>> {
    return this.taskApi
      .skip$(id, reason)
      .pipe(tap((resp) => this.emitChange(TaskEvent.Skipped, resp)));
  }

  /**
   * Sets documents only relevant for this single task.
   */
  setDocuments$(id: string, fileIds: string[]): Observable<void> {
    return this.taskApi.setDocuments$(id, fileIds);
  }

  protected emitChange(event: TaskEvent, response?: BackendResponse.Response<any>) {
    if (response && !BackendResponse.isSuccess(response)) {
      return;
    }

    this._taskChange$.next(event);
  }
}
